import { useEffect, useState } from 'react';

import { Button } from '../../../../../components/Button';
import { Modal } from '../../../../../components/Modal';
import { PhotoField, type PhotoValue } from '../../../../../components/inputs/PhotoField';
import { ApiError } from '../../../../../lib/apiError';
import { focusValidationError } from '../../../../../lib/validationFocus';
import { personsApi } from '../../personnelApi';

/**
 * The photograph's own editor.
 *
 * **It does not go through `<PersonnelSectionModal>`, and the reason is the seam.** Every
 * other section on this profile saves through `personnelApi.update`, which writes `rsys_`.
 * The photograph is the identity store's fifth field: it is written through `personsApi`, to
 * `rsys_pii`, keyed by `PersonId` rather than by the personnel record's id. Routing it
 * through the section shell would mean teaching that shell about a second database, which is
 * the shortcut the split exists to prevent.
 *
 * **Two keys, two operations.** Writing a photograph is `personnel.update`; removing one is
 * `personnel.delete`, the same split every other record in the product carries. The caller
 * passes `canRemove`, so a reader who may correct a photograph but not delete one is never
 * offered a control that would 403.
 *
 * **No Autogenerate.** It is an edit, and the server verifies the payload's magic bytes
 * against its claimed media type — a fabricated image would be claiming to be something it is
 * not, which is exactly the case the generator rules say to omit the button for.
 */
export function EditPhotoModal({
  open,
  personId,
  currentUrl,
  canRemove,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** The identity store's key — never the personnel record's id. */
  personId: string;
  /** The photograph the record has now, or null. */
  currentUrl: string | null;
  /** Whether the caller holds `personnel.delete`. */
  canRemove: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [photo, setPhoto] = useState<PhotoValue | null>(null);
  const [touched, setTouched] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open) return;

    setPhoto(null);
    setTouched(false);
    setConfirmed(false);
    setError(null);
  }, [open]);

  if (!open) return null;

  /*
    The field reports null for two different things — nothing chosen yet, and the stored
    photograph cleared — so `touched` is what separates them. Without it, opening the modal
    and pressing Save would delete a photograph nobody asked to remove.
  */
  const removing = touched && photo === null && Boolean(currentUrl) && canRemove;
  const changed = photo !== null || removing;
  const canSave = confirmed && changed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      if (photo) {
        await personsApi.setPhoto(personId, photo);
      } else {
        await personsApi.removePhoto(personId);
      }

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the photograph.', 0);

      setError(failure);
      /*
        ONE control, TWO server fields: a wrong media type is rejected as `contentType` and
        everything else about the payload as `bytes`. That is exactly the case `fieldIds`
        exists for — a rename cannot fix it, and without the mapping a type rejection would
        resolve to no element at all and silently move nothing.
      */
      focusValidationError(failure, {
        idPrefix: 'personnel-photo',
        fieldIds: { contentType: 'personnel-photo-bytes' },
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Photograph"
      size="lg"
      id="personnel-photo-modal"
      confirm={{
        id: 'personnel-photo-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button
            id="personnel-photo-cancel"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            id="personnel-photo-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          The id resolves what the server names: the payload field is `bytes`, so a rejected
          image kebabs to `personnel-photo-bytes` under this prefix.
        */}
        <PhotoField
          id="personnel-photo-bytes"
          label="Photograph"
          description="Kept in the identity store with the name and date of birth — never in the main database."
          value={photo}
          existingUrl={currentUrl}
          removable={canRemove}
          onChange={(next) => {
            setPhoto(next);
            setTouched(true);
          }}
          error={error?.fieldError('bytes') ?? error?.fieldError('contentType')}
          disabled={saving}
        />

        {removing ? (
          <p className="text-xs text-slate-500">
            Saving removes the photograph. It is kept and can be restored, like every other delete
            in the product.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
